
import Vue, { PropOptions } from 'vue';
import axios from 'axios';
import _ from 'lodash';
import { MapAutocomplete } from '@/models/page-models/page-items/MapAutocomplete';

export default Vue.extend({
  props: {
    input: { type: Object, default: () => {} } as PropOptions<MapAutocomplete>,
    data: { type: Object, default: () => ({}) },
    getVariable: { type: Function, default: () => () => ({}) },
    callFunction: { type: Function, default: () => () => {} },
    onChange: { type: Function, default: () => {} },
    parent: { type: Object, default: () => ({}) },
    rowDisabled: { type: Boolean, default: false }
  },
  data(): { 
    dialog: boolean,
    center: { lat: number, lng: number },
    marker?: { position?: { lat: number, lng: number } },
    searchLocation: string,
    searchResults: any[],
    isLoading: boolean,
    searchInput: string | null,
    selectedPlace: any,
    debouncedSearch: any
  } {
    return {
      dialog: false,
      center: { lat: 26.247897, lng: 49.989059 },
      marker: {},
      searchLocation: '',
      searchResults: [],
      isLoading: false,
      searchInput: null,
      selectedPlace: null,
      debouncedSearch: null
    };
  },
  computed: {
    label: function(): string {
      return this.input.label;
    },
    addressValue: function(): any {
      return this.getVariable(this.input.variable);
    }
  },
  watch: {
    searchInput(val) {
      if (!val) return;
      if (this.selectedPlace && val === this.selectedPlace.formatted_address) return;
      
      this.isLoading = true;
      this.debouncedSearch(val);
    },
    async addressValue() {
      if(!_.get(this.addressValue, 'alias_ar') && _.get(this.addressValue, 'formatted_address')) {
        const arabicAddress = await this.getArabicAddress(this.addressValue.formatted_address);
        this.$set(this.getVariable(this.input.variable), 'alias_ar', arabicAddress);
      }
    }
  },
  created() {
    // delay the API call
    this.debouncedSearch = _.debounce(this.fetchAddresses, 500);
  },
  async mounted() {
    try {
      await this.geolocate();
    } catch (err) {
      // eslint-disable-next-line
      const serverMessage = err.message === 'User denied Geolocation' ? 'Please allow your Location' : err.message;
      this.$store.commit('setSnackbar', { serverMessage, show: true, color: 'error' });
    }
    this.$watch(
      () => this.getVariable(this.input.variable), 
      (newPlaceResult, oldPlaceResult) => {
        if(newPlaceResult && newPlaceResult !== oldPlaceResult) {
          this.selectedPlace = newPlaceResult;
          if (newPlaceResult.formatted_address) {
            this.searchResults = [newPlaceResult];
          }
          this.updateFormVariable(newPlaceResult);
        }
      }, 
      { immediate: true, deep: true }
    );
  },
  methods: {
    // Fetches autocomplete suggestions from Google Geocoding API
    async fetchAddresses(val: string) {
      try {
        const response = await axios.get(
          `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(val)}&key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}`
        );
        this.searchResults = response.data.results;
      } catch (error) {
        console.error('Error fetching addresses:', error);
      } finally {
        this.isLoading = false;
      }
    },
    onPlaceSelected(placeResultData: any) {
      if (placeResultData) {
        this.updateFormVariable(placeResultData);
      }
    },
    updateFormVariable(placeResultData: any) {
      if(placeResultData) {
        const { prefix: prefix1, key: key1 } = this.parent._getPrefixAndKey(this.input.variable);
        const initialData = this.getVariable(prefix1);
        const { prefix, key } = this.parent._getPrefixAndKey(prefix1);
        this.searchLocation = placeResultData.formatted_address;
        if(!placeResultData.alias_en) {
          placeResultData.alias_en = placeResultData.formatted_address;
        }
        this.$set(this.getVariable(prefix), key, { ...initialData, [key1]: placeResultData });
        this.moveMarker(placeResultData);
      }
    },
    searchAddress() {
      return axios
        .get(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(this.searchLocation)}&key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}`)
        .then((response: any) => {
          if (response.data.results.length > 0) {
            this.updateFormVariable(response.data.results[0]);
          }
        })
        .catch((error: any) => {
          console.log(error);
        });
    },
    addMarker(event: any) {
      const position = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      this.marker = { position };
      this.getGeocodedAddress(position);
    },
    movePlace(event: any) {
      const location = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      this.getGeocodedAddress(location);
    },
    getGeocodedAddress(location: { lat: number, lng: number}) {
      return axios
        .get(`https://maps.googleapis.com/maps/api/geocode/json?latlng=${location.lat},${location.lng}&key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}`)
        .then((response: any) => {
          if (response.data.results.length > 0) {
            this.updateFormVariable(response.data.results[0]);
          }
        })
        .catch((error: any) => {
          console.log(error);
        });
    },
    async getArabicAddress(searchString: string): Promise<string> {
      const apiResponse: any = await axios
        .get(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(searchString)}&key=${process.env.VUE_APP_GOOGLE_MAPS_API_KEY}&language=ar-SA`);
      
      if (apiResponse.data.results.length > 0) {
        return this.getCityAndCountryFromAddress(apiResponse.data.results[0]);
      }
      return '';
    },
    getCityAndCountryFromAddress(addressObject: any): string {
      const addressComponents: any[] = _.get(addressObject, 'address_components');
      if(!addressComponents) return '';
      const cityComponent = addressComponents.find((addressComponent: any) => addressComponent.types.includes('locality') || addressComponent.types.includes('postal_town'));
      const countryComponent = addressComponents.find((addressComponent: any) => addressComponent.types.includes('country'));
      
      const city = cityComponent ? cityComponent.long_name : '';
      const country = countryComponent ? countryComponent.long_name : '';
      
      return [city, country].filter(Boolean).join(', ');
    },
    moveMarker(placeData: any) {
      if (placeData && placeData.geometry && placeData.geometry.location) {
        const marker = {
          lat: typeof placeData.geometry.location.lat === 'function' ? placeData.geometry.location.lat() : placeData.geometry.location.lat,
          lng: typeof placeData.geometry.location.lng === 'function' ? placeData.geometry.location.lng() : placeData.geometry.location.lng,
        };
        this.marker = { position: marker };
        this.center = marker;
      }
    },
    geolocate() {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          return reject(new Error('Geolocation is not supported by your browser'));
        }
        navigator.geolocation.getCurrentPosition((position) => {
          this.center = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          resolve(this.center);
        }, err => {
          reject(err);
        });
      });
    }
  }
});
